import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { applyDocumentLang, readStoredLang, setCurrentLang, translate, LANG_STORAGE_KEY, type Lang } from "@/lib/i18n";
import { useAgent } from "@/context/agent-context";

// لغة الواجهة: تفضيلٌ **لكل مستخدم** لا لكل شركة — شركةٌ فيها موظّفون عرب
// وآخرون لا يقرؤون إلا الإنجليزية، فلغةٌ واحدة للشركة تُعطّل نصفهم.
//
// المصدر بالترتيب: تفضيلٌ محفوظ في الخادم (يتبع المستخدم على أي جهاز) ←
// اختيارٌ محليّ لهذه الجلسة ← العربية. والاختيار المحليّ يُعرَض فوراً بلا
// انتظار الشبكة: تبديل اللغة يجب أن يكون فورياً لا بعد رحلة إلى الخادم.

interface LangCtx {
  lang: Lang;
  /** يبدّل لغة هذه الجلسة فقط (لا يُحفظ في الخادم). */
  setLang: (l: Lang) => void;
  /** يجعلها لغته الافتراضية على كل أجهزته. */
  saveDefault: (l: Lang) => Promise<void>;
  /**
   * هل للمستخدم لغة محفوظة؟
   *   `null`  = لم يُعرَف بعد (لم يُسأل الخادم أو لم يردّ) ⇒ **لا يُعرض شيء**.
   *   `false` = لا لغةَ محفوظة له ⇒ يُعرض عليه الاختيار.
   *   `true`  = له لغة ⇒ لا يُسأل.
   * الحالة الثالثة هي جوهر الأمر: بدونها تبدأ القيمة `false` فتُقرأ «لا لغةَ
   * له» وهي في الحقيقة «لا نعرف بعد» — فتظهر النافذة ثم تختفي حين يصل الجواب.
   */
  hasDefault: boolean | null;
  t: (text: string) => string;
}

const Ctx = createContext<LangCtx | undefined>(undefined);

export function LangProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => readStoredLang() ?? "ar");
  const [hasDefault, setHasDefault] = useState<boolean | null>(null);
  const { isAuthenticated } = useAgent();

  // أثناء الرسم لا بعده: `tt()` تُستدعى في أثناء رسم الأبناء، فلو أُجّل الضبط
  // إلى `useEffect` لرُسمت أوّل شاشة باللغة السابقة ثم قفزت.
  setCurrentLang(lang);
  useEffect(() => { applyDocumentLang(lang); }, [lang]);

  // يُسأل الخادم عند **المصادقة** لا عند الإقلاع: `login()` لا يُعيد تحميل
  // الصفحة، فالمزوّد يبقى مركَّباً من قبلها — وسؤالٌ يُطرح مرّةً عند التركيب
  // يقع ولا رمزَ بعد، فلا يُسأل الخادم أبداً بعد تسجيل الدخول.
  useEffect(() => {
    if (!isAuthenticated) { setHasDefault(null); return undefined; }
    const token = localStorage.getItem("waslacrm_token");
    if (!token) { setHasDefault(null); return undefined; }
    let alive = true;
    fetch("/api/my-language", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d: { lang?: string | null }) => {
        if (!alive) return;
        if (d?.lang === "ar" || d?.lang === "en") {
          setHasDefault(true);
          setLangState(d.lang);
          try { localStorage.setItem(LANG_STORAGE_KEY, d.lang); } catch { /* وضع التصفّح الخاص */ }
        } else {
          setHasDefault(false); // ردٌّ صريح: لا لغةَ محفوظة — الآن يُعرض الاختيار
        }
      })
      // تعذّر السؤال ⇒ تبقى الحال مجهولة، فلا نعرض اختياراً قد يكون محسوماً.
      .catch(() => { if (alive) setHasDefault(null); });
    return () => { alive = false; };
  }, [isAuthenticated]);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try { localStorage.setItem(LANG_STORAGE_KEY, l); } catch { /* وضع التصفّح الخاص */ }
  }, []);

  const saveDefault = useCallback(async (l: Lang) => {
    setLang(l);
    setHasDefault(true);
    const token = localStorage.getItem("waslacrm_token");
    if (!token) return;
    await fetch("/api/my-language", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ lang: l }),
    }).catch(() => { /* الحفظ تفضيلٌ لا عملية حرجة — المحليّ يكفي حتى تعود الشبكة */ });
  }, [setLang]);

  const t = useCallback((text: string) => translate(text, lang), [lang]);

  return (
    <Ctx.Provider value={{ lang, setLang, saveDefault, hasDefault, t }}>
      {children}
    </Ctx.Provider>
  );
}

export function useLang(): LangCtx {
  const v = useContext(Ctx);
  // بلا مزوّد: العربية بلا كسر. أهون من إسقاط الصفحة كلّها لأن مكوّناً
  // استُعمل خارج الشجرة (اختبار، أو معاينة مكوّن وحده).
  if (!v) {
    return {
      lang: "ar", setLang: () => {}, saveDefault: async () => {},
      hasDefault: true, t: (s: string) => s,
    };
  }
  return v;
}
